import { readContractorCapability } from './capability';
import { composeApiRequestUrl } from './config/api-base-url';
import { readJson } from './http';

export interface Problem {
  readonly code?: string;
  readonly title?: string;
  readonly detail?: string;
  readonly errors?: readonly { field: string; code: string; message?: string }[];
  readonly expected_version?: string | null;
  readonly current_version?: string | null;
  readonly stale_aggregates?: readonly {
    readonly aggregate_type?: string;
    readonly aggregate_id?: string;
    readonly expected_version?: string;
    readonly current_version?: string;
  }[];
}

export type JsonValue =
  string | number | boolean | null | readonly JsonValue[] | { readonly [key: string]: JsonValue };

export type FieldOwnerCode = 'CONTRACTOR' | 'INSPECTOR' | 'SYSTEM';
export type FieldBindingKind = 'CASE_SNAPSHOT' | 'CASE_DYNAMIC' | 'CHECKLIST_DYNAMIC';
export type ConfigurationFreshnessStatus = 'CURRENT' | 'CONFIGURATION_REFRESH_REQUIRED';
export type DocumentCaptureMode = 'METADATA_PREFILL' | 'PRESENCE_ONLY';

export interface ValidationErrorDto {
  readonly field: string;
  readonly code: string;
  readonly message?: string;
}

export interface ResolvedContractorFieldDto {
  readonly field_key: string;
  readonly binding_kind: FieldBindingKind;
  readonly label: string;
  readonly value_type: string;
  readonly owner_code: FieldOwnerCode;
  readonly required: boolean;
  readonly applicable: boolean;
  readonly validation: Record<string, JsonValue>;
  readonly placement: string;
  readonly semantic_key?: string | null;
  readonly snapshot_path?: string | null;
  readonly checklist_template_id?: string | null;
  readonly template_version_id?: string | null;
  readonly field_definition_id?: string | null;
}

export interface TemplateDocumentMetadataFieldDefinitionDto {
  readonly id: string;
  readonly document_requirement_id: string;
  readonly field_code: string;
  readonly label: string;
  readonly value_type: string;
  readonly required_rule: Record<string, JsonValue>;
  readonly applicability_rule: Record<string, JsonValue>;
  readonly validation: Record<string, JsonValue>;
  readonly ordinal: number;
  readonly help_text?: string | null;
}

export interface ResolvedContractorDocumentRequirementDto {
  readonly document_type_id: string;
  readonly document_type_code: string;
  readonly display_label: string;
  readonly subject_binding: string;
  readonly capture_mode: DocumentCaptureMode;
  readonly required: boolean;
  readonly applicable: boolean;
  readonly metadata_fields: readonly TemplateDocumentMetadataFieldDefinitionDto[];
}

export interface ResolvedContractorFormSchemaDto {
  readonly configuration_fingerprint: string;
  readonly field_groups: Record<string, JsonValue>;
  readonly fields: readonly ResolvedContractorFieldDto[];
  readonly document_requirements: readonly ResolvedContractorDocumentRequirementDto[];
  readonly validation_summary: Record<string, JsonValue>;
}

export interface ContractorConfigurationDto {
  readonly plan_revision_id: string;
  readonly plan_revision_number: number;
  readonly configuration_fingerprint: string;
  readonly equipment_type_id: string;
  readonly equipment_type_name: string;
  readonly inspection_site_id: string;
  readonly inspection_site_name: string;
  readonly package_revision_id: string;
  readonly template_versions: readonly {
    readonly checklist_template_id: string;
    readonly template_version_id: string;
    readonly template_definition_checksum: string;
    readonly ordinal: number;
  }[];
  readonly freshness: ConfigurationFreshnessStatus;
  readonly refresh_reason?: string | null;
}

export interface ContractorInspectionSiteOptionDto {
  readonly id: string;
  readonly code: string;
  readonly display_name: string;
}

export interface ContractorEquipmentTypeOptionDto {
  readonly id: string;
  readonly code: string;
  readonly display_name: string;
  readonly checklist_count?: number;
}

export interface ContractorFieldValueInputDto {
  readonly field_key: string;
  readonly value: JsonValue;
}

export interface ContractorDocumentValueInputDto {
  readonly document_type_id: string;
  readonly subject_binding: string;
  readonly metadata: Record<string, JsonValue>;
}

export interface UpdateContractorPrefillRequest {
  readonly expected_case_version: string;
  readonly expected_prefill_version: string;
  readonly configuration_plan_revision_id: string;
  readonly configuration_fingerprint: string;
  readonly inspection_site_id: string;
  readonly equipment_type_id: string;
  readonly field_values: readonly ContractorFieldValueInputDto[];
  readonly document_values: readonly ContractorDocumentValueInputDto[];
  readonly changes?: readonly { field_code: string; value: JsonValue }[];
}

export interface SubmitContractorPrefillRequest {
  readonly expected_case_version: string;
  readonly expected_prefill_version: string;
  readonly configuration_plan_revision_id: string;
  readonly configuration_fingerprint: string;
}

export interface PreviewContractorConfigurationRebaseRequest {
  readonly expected_case_version: string;
  readonly expected_prefill_version: string;
  /** null when unbound; exact server fingerprint when bound. Never client-synthesized. */
  readonly current_configuration_fingerprint: string | null;
  readonly target_equipment_type_id: string;
  readonly target_inspection_site_id: string;
}

export interface RebaseContractorPrefillConfigurationRequest {
  readonly expected_case_version: string;
  readonly expected_prefill_version: string;
  /** null when unbound; exact server fingerprint when bound. Never client-synthesized. */
  readonly current_configuration_fingerprint: string | null;
  readonly target_equipment_type_id: string;
  readonly target_inspection_site_id: string;
  readonly impact_checksum: string;
}

export interface ContractorConfigurationDiffDto {
  readonly preserved_fields: readonly string[];
  readonly added_fields: readonly string[];
  readonly removed_fields: readonly string[];
  readonly incompatible_fields: readonly string[];
  readonly preserved_documents: readonly string[];
  readonly added_documents: readonly string[];
  readonly removed_documents: readonly string[];
  readonly incompatible_documents: readonly string[];
}

export interface ContractorConfigurationRebasePreviewDto {
  readonly old_configuration_fingerprint: string | null;
  readonly target_configuration_fingerprint: string;
  readonly impact_checksum: string;
  readonly diff: ContractorConfigurationDiffDto;
  readonly preserved_value_count: number;
  readonly new_required_value_count: number;
  readonly incompatible_value_count: number;
}

export type ContractorReuseResolutionState =
  'RESOLVED_SINGLE' | 'RESOLVED_MULTI' | 'UNRESOLVED' | 'REVIEW_REQUIRED';

export interface ContractorReuseContextDto {
  readonly resolution_state: ContractorReuseResolutionState;
  readonly contractor_options: readonly {
    readonly contractor_profile_id: string;
    readonly display_name: string;
  }[];
  readonly selected_contractor_profile_id: string | null;
  readonly vehicle_options: readonly {
    readonly vehicle_profile_id: string;
    readonly display_name: string;
  }[];
  readonly selected_vehicle_profile_id: string | null;
}

export interface SelectContractorReuseProfileRequest {
  readonly contractor_profile_id: string;
  readonly expected_case_version: string;
}

export interface SelectVehicleReuseProfileRequest {
  readonly vehicle_profile_id: string;
  readonly expected_case_version: string;
}

export interface PrefillDto {
  readonly inspection_case_id: string;
  readonly contractor_edit_cutoff_at: string;
  readonly booking: {
    readonly status: string;
    readonly scheduled_start_at: string;
    readonly id?: string;
    readonly rubitime_record_id?: string;
    readonly scheduled_end_at?: string | null;
    readonly version?: string;
  };
  readonly state: 'DRAFT' | 'SUBMITTED';
  readonly data: Record<string, JsonValue>;
  readonly validation_errors: readonly ValidationErrorDto[];
  readonly case_version: string;
  readonly reuse_context?: ContractorReuseContextDto;
  readonly configuration?: ContractorConfigurationDto | null;
  readonly resolved_form_schema?: ResolvedContractorFormSchemaDto | null;
  readonly selected_inspection_site?: {
    readonly inspection_site_id: string;
    readonly code: string;
    readonly display_name: string;
  } | null;
  readonly selected_equipment_type?: {
    readonly equipment_type_id: string;
    readonly code: string;
    readonly display_name: string;
  } | null;
  readonly document_requirements?: readonly ResolvedContractorDocumentRequirementDto[];
  readonly prepared_inspection_plan?: Record<string, JsonValue> | null;
  readonly prefill_version?: string;
}

export interface ContractorConfigurationRebaseResultDto {
  readonly configuration: ContractorConfigurationDto;
  readonly prefill: PrefillDto;
  readonly diff: ContractorConfigurationDiffDto;
  readonly unresolved_required_fields: readonly string[];
}

export type ConfigurationRevalidationStatus = 'OPEN' | 'RESOLVED' | 'OBSOLETE';

/** OpenAPI ContractorRevalidationDto (1.3.4+): case_version is required Version. */
export interface ContractorRevalidationDto {
  readonly inspection_case_id: string;
  readonly revalidation_requirement_id: string;
  readonly status: ConfigurationRevalidationStatus;
  readonly required_configuration_fingerprint: string;
  readonly fields: readonly ResolvedContractorFieldDto[];
  readonly document_requirements: readonly ResolvedContractorDocumentRequirementDto[];
  readonly version: string;
  readonly case_version: string;
}

export interface UpdateContractorRevalidationRequest {
  readonly expected_case_version: string;
  readonly expected_revalidation_version: string;
  readonly configuration_fingerprint: string;
  readonly field_values: readonly ContractorFieldValueInputDto[];
  readonly document_values: readonly ContractorDocumentValueInputDto[];
}

export interface SubmitContractorRevalidationRequest {
  readonly expected_case_version: string;
  readonly expected_revalidation_version: string;
  readonly configuration_fingerprint: string;
}

export class ApiError extends Error {
  public constructor(
    public readonly status: number,
    public readonly problem: Problem,
  ) {
    super(problem.code ?? 'REQUEST_FAILED');
    this.name = 'ApiError';
  }
}

export function contractorRequestHeaders(capability: string): Record<string, string> {
  return {
    Accept: 'application/json',
    'X-Contractor-Capability': capability,
  };
}

async function contractorFetch(
  baseUrl: string,
  method: string,
  path: string,
  options: { body?: unknown; idempotencyKey?: string } = {},
): Promise<{ status: number; ok: boolean; body: unknown }> {
  const capability = readContractorCapability();
  if (capability === null) {
    throw new ApiError(403, { code: 'CAPABILITY_INVALID' });
  }
  const headers = contractorRequestHeaders(capability);
  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  if (options.idempotencyKey !== undefined) {
    headers['Idempotency-Key'] = options.idempotencyKey;
  }
  return readJson(composeApiRequestUrl(baseUrl, path), {
    method,
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  });
}

function asPrefill(body: unknown): PrefillDto {
  return body as PrefillDto;
}

export function createContractorApi(baseUrl: string) {
  const requestPrefill = async (
    method: string,
    path: string,
    options: { body?: unknown; idempotencyKey?: string } = {},
  ): Promise<PrefillDto> => {
    const response = await contractorFetch(baseUrl, method, path, options);
    const payload = response.body as PrefillDto & Problem;
    if (!response.ok) {
      throw new ApiError(response.status, payload);
    }
    return asPrefill(payload);
  };

  const requestJson = async <T>(
    method: string,
    path: string,
    options: { body?: unknown; idempotencyKey?: string } = {},
  ): Promise<T> => {
    const response = await contractorFetch(baseUrl, method, path, options);
    const payload = response.body as T & Problem;
    if (!response.ok) {
      throw new ApiError(response.status, payload);
    }
    return payload as T;
  };

  return {
    /** operationId: getContractorPrefill — GET /contractor/prefill */
    getPrefill: () => requestPrefill('GET', '/contractor/prefill'),

    /** operationId: updateContractorPrefill — PATCH /contractor/prefill */
    updatePrefill: (body: UpdateContractorPrefillRequest) =>
      requestPrefill('PATCH', '/contractor/prefill', {
        idempotencyKey: crypto.randomUUID(),
        body,
      }),

    /** operationId: submitContractorPrefill — POST /contractor/prefill/submit */
    submitPrefill: (body: SubmitContractorPrefillRequest) =>
      requestPrefill('POST', '/contractor/prefill/submit', {
        idempotencyKey: crypto.randomUUID(),
        body,
      }),

    /** operationId: listContractorInspectionSites — GET /contractor/inspection-sites */
    listInspectionSites: () =>
      requestJson<ContractorInspectionSiteOptionDto[]>('GET', '/contractor/inspection-sites'),

    /** operationId: listContractorEquipmentTypes — GET /contractor/equipment-types */
    listEquipmentTypes: () =>
      requestJson<ContractorEquipmentTypeOptionDto[]>('GET', '/contractor/equipment-types'),

    /** operationId: previewContractorConfigurationRebase — POST /contractor/prefill/rebase-preview */
    previewRebase: (body: PreviewContractorConfigurationRebaseRequest) =>
      requestJson<ContractorConfigurationRebasePreviewDto>(
        'POST',
        '/contractor/prefill/rebase-preview',
        { body },
      ),

    /** operationId: rebaseContractorPrefillConfiguration — POST /contractor/prefill/rebase */
    rebaseConfiguration: (body: RebaseContractorPrefillConfigurationRequest) =>
      requestJson<ContractorConfigurationRebaseResultDto>('POST', '/contractor/prefill/rebase', {
        idempotencyKey: crypto.randomUUID(),
        body,
      }),

    /** operationId: selectContractorReuseProfile — POST /contractor/prefill/reuse/contractor */
    selectContractorReuse: (body: SelectContractorReuseProfileRequest) =>
      requestPrefill('POST', '/contractor/prefill/reuse/contractor', {
        idempotencyKey: crypto.randomUUID(),
        body,
      }),

    /** operationId: selectVehicleReuseProfile — POST /contractor/prefill/reuse/vehicle */
    selectVehicleReuse: (body: SelectVehicleReuseProfileRequest) =>
      requestPrefill('POST', '/contractor/prefill/reuse/vehicle', {
        idempotencyKey: crypto.randomUUID(),
        body,
      }),

    /** operationId: getContractorRevalidation — GET /contractor/revalidation */
    getRevalidation: () =>
      requestJson<ContractorRevalidationDto>('GET', '/contractor/revalidation'),

    /** operationId: updateContractorRevalidation — PATCH /contractor/revalidation */
    updateRevalidation: (body: UpdateContractorRevalidationRequest) =>
      requestJson<ContractorRevalidationDto>('PATCH', '/contractor/revalidation', {
        idempotencyKey: crypto.randomUUID(),
        body,
      }),

    /** operationId: submitContractorRevalidation — POST /contractor/revalidation/submit */
    submitRevalidation: (body: SubmitContractorRevalidationRequest) =>
      requestJson<ContractorRevalidationDto>('POST', '/contractor/revalidation/submit', {
        idempotencyKey: crypto.randomUUID(),
        body,
      }),
  };
}

export type ContractorApi = ReturnType<typeof createContractorApi>;
